import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { ButtonBase, Box, type SxProps, type Theme } from '@mui/material'
import { CleanThemeProvider } from 'shared/theme/cleanThemeProvider'

export interface ButtonProps {
  children: React.ReactNode
  variant?: 'text' | 'primary' | 'dark' | 'contained' | 'outlined'
  startIcon?: React.ReactNode
  showBadge?: boolean
  sparkleEffect?: boolean
  onClick?: () => void
  disabled?: boolean
  fullWidth?: boolean
  /** テナント別ブランディング等で背景色を上書きする(例: sooki の #1C8DCA)。disabled 時は適用しない */
  bgcolorOverride?: string
  /** 利用箇所固有のFigma値を、既定・variantスタイルの後段で上書きする */
  sx?: SxProps<Theme>
  /** トグルボタンとして利用するときの選択状態 */
  ariaPressed?: boolean
  /** 表示テキストだけでは操作目的が伝わらない場合のアクセシブル名 */
  ariaLabel?: string
}

interface SparkleParticle {
  id: number
  x: number
  y: number
  size: number
  delayMs: number
}

function ButtonContent ({
  children,
  variant = 'text',
  startIcon,
  showBadge = false,
  sparkleEffect = false,
  onClick,
  disabled = false,
  fullWidth = false,
  bgcolorOverride,
  sx = [],
  ariaPressed,
  ariaLabel
}: ButtonProps) {
  const [sparkles, setSparkles] = useState<SparkleParticle[]>([])
  const sparkleIdRef = useRef(0)
  const sparkleTimeoutsRef = useRef<number[]>([])

  useEffect(() => {
    return () => {
      for (const timeoutId of sparkleTimeoutsRef.current) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [])

  const createSparkles = useCallback(() => {
    if (!sparkleEffect || disabled) return

    const sparkleCount = 8
    const newSparkles: SparkleParticle[] = Array.from({ length: sparkleCount }, (_, index) => ({
      id: sparkleIdRef.current + index,
      x: Math.random() * 96 - 48,
      y: Math.random() * 96 - 48,
      size: Math.random() * 6 + 4,
      delayMs: Math.random() * 200
    }))

    sparkleIdRef.current += sparkleCount
    setSparkles((previous) => [...previous, ...newSparkles])

    const newSparkleIds = new Set(newSparkles.map((sparkle) => sparkle.id))
    const timeoutId = window.setTimeout(() => {
      setSparkles((previous) => previous.filter((sparkle) => !newSparkleIds.has(sparkle.id)))
      sparkleTimeoutsRef.current = sparkleTimeoutsRef.current.filter((id) => id !== timeoutId)
    }, 1000)
    sparkleTimeoutsRef.current.push(timeoutId)
  }, [disabled, sparkleEffect])

  const handleClick = useCallback(() => {
    createSparkles()
    onClick?.()
  }, [createSparkles, onClick])

  const sparkleHoverStyles: Record<string, unknown> = {}
  if (sparkleEffect) {
    sparkleHoverStyles['&:hover .button-sparkle-shimmer'] = {
      animation: 'buttonSparkleShimmer 1.2s ease-in-out'
    }
  }

  // Variant スタイルの定義
  const variantStyles = {
    text: {
      bgcolor: 'transparent',
      color: '#353535',
      '&:hover': {
        bgcolor: 'rgba(0, 0, 0, 0.04)'
      }
    },
    primary: {
      bgcolor: '#3863ff',
      color: '#fcfcfc',
      '&:hover': {
        bgcolor: '#2952e6'
      }
    },
    dark: {
      bgcolor: '#1a1a1a',
      color: '#fcfcfc',
      '&:hover': {
        bgcolor: '#2a2a2a'
      }
    },
    contained: {
      bgcolor: '#f0f0f0',
      color: '#353535',
      '&:hover': {
        bgcolor: '#e0e0e0'
      }
    },
    // Figma: アウトライン(SSO ログイン等)。枠 rgba(0,0,0,0.6) / 透明背景 / 文字 secondary
    outlined: {
      bgcolor: 'transparent',
      color: 'rgba(0, 0, 0, 0.6)',
      border: '1px solid rgba(0, 0, 0, 0.6)',
      '&:hover': {
        bgcolor: 'rgba(0, 0, 0, 0.04)'
      }
    }
  }

  return (
    <ButtonBase
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      disabled={disabled}
      onClick={handleClick}
      onMouseEnter={createSparkles}
      sx={[
        {
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          px: 1.5,
          py: 1,
          height: 48,
          borderRadius: '4px',
          fontFamily: '"Noto Sans JP", sans-serif',
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1,
          overflow: 'hidden',
          width: fullWidth ? '100%' : 'auto',
          minWidth: 80,
          maxWidth: '100%',
          ...variantStyles[variant],
          // テナント別の背景色上書き(disabled は後段の &:disabled で Figma グレーが優先される)
          ...(bgcolorOverride != null
            ? { bgcolor: bgcolorOverride, '&:hover': { bgcolor: bgcolorOverride } }
            : {}),
          '@keyframes buttonSparkleBurst': {
            '0%': {
              transform: 'translate(-50%, -50%) scale(0)',
              opacity: 0.95
            },
            '100%': {
              transform: 'translate(calc(-50% + var(--sparkle-x)), calc(-50% + var(--sparkle-y))) scale(1)',
              opacity: 0
            }
          },
          '@keyframes buttonSparkleShimmer': {
            '0%': {
              transform: 'translateX(-140%)',
              opacity: 0
            },
            '30%': {
              opacity: 0.45
            },
            '70%': {
              opacity: 0.35
            },
            '100%': {
              transform: 'translateX(160%)',
              opacity: 0
            }
          },
          ...sparkleHoverStyles,
          // Figma: 非活性は design token color/action/disabled (rgba(0,0,0,0.38)) を使う。
          // 塗りのある variant はグレー背景 + 白文字、text variant はグレー文字にする。
          // NOTE: MUI ButtonBase は disabled 時に pointer-events:none を付けるため
          // cursor 指定は効かない。背景/文字色のみ Figma の disabled token に合わせる。
          '&:disabled': {
            ...(variant === 'text'
              ? { bgcolor: 'transparent', color: 'rgba(0, 0, 0, 0.38)' }
              : { bgcolor: 'rgba(0, 0, 0, 0.38)', color: '#fcfcfc' })
          }
        },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      {/* 左側アイコン */}
      {startIcon != null && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20
          }}
        >
          {startIcon}
        </Box>
      )}

      {/* テキスト */}
      <Box
        component="span"
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'visible',
          textOverflow: 'unset',
          textAlign: 'center'
        }}
      >
        {children}
      </Box>

      {/* シマー演出 */}
      {sparkleEffect && (
        <Box
          className="button-sparkle-shimmer"
          data-testid="sparkle-shimmer"
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            transform: 'translateX(-140%)',
            opacity: 0,
            background: 'linear-gradient(120deg, rgba(255,255,255,0), rgba(255,255,255,0.45), rgba(255,255,255,0))'
          }}
        />
      )}

      {/* バッジ（右上のドット） */}
      {showBadge && (
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: '#3863FF'
          }}
        />
      )}

      {/* キラキラ粒子 */}
      {sparkles.map((sparkle) => {
        const sparkleStyle: CSSProperties = {
          '--sparkle-x': `${sparkle.x}px`,
          '--sparkle-y': `${sparkle.y}px`,
          animationDelay: `${sparkle.delayMs}ms`,
          width: `${sparkle.size}px`,
          height: `${sparkle.size}px`
        } as CSSProperties

        return (
          <Box
            className="button-sparkle-particle"
            data-testid="sparkle-particle"
            key={sparkle.id}
            style={sparkleStyle}
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              borderRadius: '50%',
              bgcolor: '#FFF3A1',
              boxShadow: '0 0 8px rgba(255, 243, 161, 0.8)',
              pointerEvents: 'none',
              transform: 'translate(-50%, -50%) scale(0)',
              opacity: 0,
              animation: 'buttonSparkleBurst 900ms ease-out forwards'
            }}
          />
        )
      })}
    </ButtonBase>
  )
}

/**
 * Figma デザインに基づいたボタンコンポーネント
 *
 * @param variant - ボタンのスタイル
 *   - 'text': 背景なし、文字色 #353535
 *   - 'primary': 背景青 #3863ff、文字色白
 *   - 'dark': 背景黒 #1a1a1a、文字色白
 *   - 'contained': デフォルト背景
 * @param startIcon - 左側に表示するアイコン
 * @param showBadge - 右上に青いドットを表示
 * @param sparkleEffect - hover/click 時にキラキラ演出を表示
 * @param onClick - クリックハンドラー
 * @param disabled - 無効状態
 * @param fullWidth - 幅いっぱいに表示
 *
 * @example
 * ```tsx
 * // テキストのみ
 * <Button variant="text">フィルタ</Button>
 *
 * // アイコン付き
 * <Button variant="text" startIcon={<FilterIcon />}>フィルタ</Button>
 *
 * // バッジ付き
 * <Button variant="text" startIcon={<FilterIcon />} showBadge>フィルタ</Button>
 *
 * // キラキラ演出付き
 * <Button variant="primary" sparkleEffect>検知条件追加</Button>
 *
 * // 背景青
 * <Button variant="primary">検索する</Button>
 *
 * // 背景黒 + アイコン
 * <Button variant="dark" startIcon={<AddIcon />}>ユーザーを招待</Button>
 * ```
 */
export default function Button (props: ButtonProps) {
  return (
    <CleanThemeProvider>
      <ButtonContent {...props} />
    </CleanThemeProvider>
  )
}
